/**
 * Challenges: Weekly, Season and Event.
 *
 * ===========================================================================
 * TIME IS AN ARGUMENT, NEVER A READING
 * ===========================================================================
 *
 * Every function here takes `nowMs`. Nothing in this file calls `Date.now`, for
 * the same reason nothing in src/physics does: a weekly that refreshes on
 * Wednesday cannot be tested by a suite that has no way to be on a Wednesday,
 * and a year boundary is not a thing you can wait for. The caller owns the
 * clock — `ProgressionRuntime` in src/ui/progression.ts is the only place in
 * the shipped game that reads one.
 *
 * All of the arithmetic is UTC. A local-midnight refresh would mean the same
 * player's week ends at a different instant depending on where they opened the
 * tab, and a challenge that expires an hour early because the clocks went
 * forward is the kind of bug nobody ever reproduces.
 *
 * ===========================================================================
 * THE THREE KINDS
 * ===========================================================================
 *
 * WEEKLY — `WEEKLY_FREE_COUNT` (3) drawn from the free pool plus
 *   `WEEKLY_PREMIUM_COUNT` (3) from the premium pool, chosen deterministically
 *   from the week index, refreshing on `WEEKLY_REFRESH_DAY` (Wednesday) and
 *   expiring at the end of that week. The premium three are not merely locked
 *   without the pass: they are not in the list at all, which is what the real
 *   game does and is the only honest rendering of "you do not have these".
 *
 * SEASON — `SEASON_STAGES` (4) stages of three. Stage 1 is open from the start;
 *   stage k opens when every challenge in stage k-1 is COMPLETE. Gating on
 *   completion rather than on a date is what makes the stages a ladder instead
 *   of a calendar, and it is what the real game's season challenges do.
 *
 * EVENT — a limited window inside the season, given as day offsets from the
 *   season start so an event is live in every season rather than in one that
 *   has already gone. `ChallengeDef` has no window fields (it is a contract
 *   this file does not edit), so the window lives beside the def in
 *   `EventChallenge`.
 *
 * ===========================================================================
 * PROGRESS IS A STAT KEY AND A THRESHOLD
 * ===========================================================================
 *
 * The contract is explicit that a challenge is a counter over `MatchStats` and
 * not an arbitrary predicate, and that this is what lets progress be computed
 * from a match summary, persisted, and shown as "3 / 10". Every def below
 * respects it. If a challenge you want cannot be written as a stat and a
 * target, the stat goes into `MatchStats` first — see src/game/match-stats.ts,
 * which is where "a save" is defined exactly once.
 */

import { Rng } from '../physics/rng';
import {
  CHALLENGE_TIER_POINTS,
  SEASON_STAGES,
  WEEKLY_FREE_COUNT,
  WEEKLY_PREMIUM_COUNT,
  WEEKLY_REFRESH_DAY,
  type ChallengeDef,
  type ChallengeProgress,
  type ChallengeReward,
  type MatchStats,
  type PlayerProfile,
  type Rarity,
} from './cosmetics';
import { rollDrop } from './drops';
import { grantDrops, grantItem } from './inventory';
import { addTierPoints, grantXp } from './rocketpass';

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

export const DAY_MS = 86_400_000;
export const WEEK_MS = 7 * DAY_MS;

/**
 * Day of week, 0 = Sunday, for a UTC instant.
 *
 * 1970-01-01 was a Thursday, hence the +4. The double modulo is not
 * decoration: `%` in JavaScript keeps the sign of the dividend, so a pre-epoch
 * instant would otherwise come out negative and land the refresh on the wrong
 * day of the week for every date before 1970. Cheap, and it makes the function
 * total.
 */
export const utcDayOfWeek = (nowMs: number): number => {
  const days = Math.floor(nowMs / DAY_MS);
  return (((days + 4) % 7) + 7) % 7;
};

/** UTC midnight at the start of the challenge week containing `nowMs`. */
export const weekStart = (nowMs: number): number => {
  const days = Math.floor(nowMs / DAY_MS);
  const back = ((utcDayOfWeek(nowMs) - WEEKLY_REFRESH_DAY) % 7 + 7) % 7;
  return (days - back) * DAY_MS;
};

/** The instant the current weeklies expire and the next three arrive. */
export const weekEnd = (nowMs: number): number => weekStart(nowMs) + WEEK_MS;

/**
 * A stable index for the week, for seeding the draw.
 *
 * `weekStart` moves by exactly `WEEK_MS` from one week to the next, so this
 * increments by exactly one — which is the only property the draw needs of it.
 */
export const weekIndex = (nowMs: number): number => Math.floor(weekStart(nowMs) / WEEK_MS);

/**
 * The first season's start: Wednesday 7 January 2026, UTC midnight.
 *
 * Aligned to the weekly refresh day on purpose, so a season boundary and a
 * weekly boundary are the same instant and a player never gets a stub week.
 */
export const SEASON_EPOCH_MS = Date.UTC(2026, 0, 7);

/** Twelve weeks, matching the weekly cadence exactly. */
export const SEASON_LENGTH_DAYS = 84;
export const SEASON_MS = SEASON_LENGTH_DAYS * DAY_MS;

export interface SeasonWindow {
  /** 0 for the first season. Negative before the epoch, which is legal. */
  index: number;
  startsAt: number;
  endsAt: number;
}

/** The season containing `nowMs`. Seasons roll forever; there is always one. */
export const seasonWindow = (nowMs: number): SeasonWindow => {
  const index = Math.floor((nowMs - SEASON_EPOCH_MS) / SEASON_MS);
  const startsAt = SEASON_EPOCH_MS + index * SEASON_MS;
  return { index, startsAt, endsAt: startsAt + SEASON_MS };
};

// ---------------------------------------------------------------------------
// The definitions
// ---------------------------------------------------------------------------

const xp = (amount: number): ChallengeReward => ({ kind: 'xp', amount });
const credits = (amount: number): ChallengeReward => ({ kind: 'credits', amount });
const drop = (rarity: Rarity): ChallengeReward => ({ kind: 'drop', rarity });

/**
 * The weekly FREE pool.
 *
 * Twelve of them for three slots, so a week rarely repeats the previous one,
 * and spread across the stat keys rather than piled onto goals: a set where
 * every row is "score N" is a set that plays itself.
 */
export const WEEKLY_FREE_POOL: readonly ChallengeDef[] = [
  {
    id: 'w-goals-3',
    kind: 'weekly',
    difficulty: 'easy',
    name: 'Find The Net',
    description: 'Score 3 goals.',
    stat: 'goals',
    target: 3,
    reward: xp(400),
  },
  {
    id: 'w-saves-3',
    kind: 'weekly',
    difficulty: 'easy',
    name: 'Last Line',
    description: 'Make 3 saves.',
    stat: 'saves',
    target: 3,
    reward: xp(400),
  },
  {
    id: 'w-shots-10',
    kind: 'weekly',
    difficulty: 'easy',
    name: 'Test The Keeper',
    description: 'Take 10 shots on target.',
    stat: 'shots',
    target: 10,
    reward: xp(400),
  },
  {
    id: 'w-touches-150',
    kind: 'weekly',
    difficulty: 'easy',
    name: 'On The Ball',
    description: 'Touch the ball 150 times.',
    stat: 'touches',
    target: 150,
    reward: credits(20),
  },
  {
    id: 'w-boost-100',
    kind: 'weekly',
    difficulty: 'easy',
    name: 'Topped Up',
    description: 'Collect 100 boost pads.',
    stat: 'boostCollected',
    target: 100,
    reward: xp(350),
  },
  {
    id: 'w-matches-5',
    kind: 'weekly',
    difficulty: 'easy',
    name: 'Clock In',
    description: 'Play 5 matches.',
    stat: 'matches',
    target: 5,
    reward: xp(400),
  },
  {
    // The one playlist-specific row, which is what `ChallengeDef.modes` is for.
    // The mode name is `GameMode` in ./settings, verbatim.
    id: 'w-duel-wins-3',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'One On One',
    description: 'Win 3 Duel matches.',
    stat: 'wins',
    target: 3,
    modes: ['duel'],
    reward: credits(30),
  },
  {
    id: 'w-assists-5',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'Set It Up',
    description: 'Get 5 assists.',
    stat: 'assists',
    target: 5,
    reward: credits(40),
  },
  {
    id: 'w-wins-4',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'Winning Habit',
    description: 'Win 4 matches.',
    stat: 'wins',
    target: 4,
    reward: credits(40),
  },
  {
    id: 'w-demos-5',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'Wrecking Ball',
    description: 'Demolish 5 opponents.',
    stat: 'demolitions',
    target: 5,
    reward: xp(700),
  },
  {
    id: 'w-distance-200k',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'The Long Way',
    description: 'Drive 200,000 uu.',
    stat: 'distance',
    target: 200_000,
    reward: xp(700),
  },
  {
    id: 'w-aerial-2',
    kind: 'weekly',
    difficulty: 'hard',
    name: 'Off The Ground',
    description: 'Score 2 aerial goals.',
    stat: 'aerialGoals',
    target: 2,
    reward: credits(60),
  },
  {
    id: 'w-mvp-2',
    kind: 'weekly',
    difficulty: 'hard',
    name: 'Best On The Pitch',
    description: 'Earn 2 MVPs.',
    stat: 'mvps',
    target: 2,
    reward: credits(60),
  },
];

/**
 * The weekly PREMIUM pool. Harder, and worth more — an average premium week is
 * 18 Tier Points against the free three's 11.
 */
export const WEEKLY_PREMIUM_POOL: readonly ChallengeDef[] = [
  {
    id: 'wp-goals-10',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'Ten Past',
    description: 'Score 10 goals.',
    stat: 'goals',
    target: 10,
    premiumOnly: true,
    reward: credits(50),
  },
  {
    id: 'wp-saves-10',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'Brick Wall',
    description: 'Make 10 saves.',
    stat: 'saves',
    target: 10,
    premiumOnly: true,
    reward: credits(50),
  },
  {
    id: 'wp-assists-10',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'Playmaker',
    description: 'Get 10 assists.',
    stat: 'assists',
    target: 10,
    premiumOnly: true,
    reward: credits(50),
  },
  {
    id: 'wp-shots-30',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'Shooting Gallery',
    description: 'Take 30 shots on target.',
    stat: 'shots',
    target: 30,
    premiumOnly: true,
    reward: xp(800),
  },
  {
    id: 'wp-wins-8',
    kind: 'weekly',
    difficulty: 'hard',
    name: 'On A Run',
    description: 'Win 8 matches.',
    stat: 'wins',
    target: 8,
    premiumOnly: true,
    reward: credits(80),
  },
  {
    id: 'wp-aerial-5',
    kind: 'weekly',
    difficulty: 'hard',
    name: 'Air Superiority',
    description: 'Score 5 aerial goals.',
    stat: 'aerialGoals',
    target: 5,
    premiumOnly: true,
    reward: drop('veryRare'),
  },
  {
    id: 'wp-demos-15',
    kind: 'weekly',
    difficulty: 'hard',
    name: 'Demolition Derby',
    description: 'Demolish 15 opponents.',
    stat: 'demolitions',
    target: 15,
    premiumOnly: true,
    reward: credits(80),
  },
  {
    id: 'wp-long-3',
    kind: 'weekly',
    difficulty: 'hard',
    name: 'From Distance',
    description: 'Score 3 goals from your own half.',
    stat: 'longGoals',
    target: 3,
    premiumOnly: true,
    reward: credits(80),
  },
  {
    id: 'wp-seconds-7200',
    kind: 'weekly',
    difficulty: 'medium',
    name: 'Two Hours In',
    description: 'Play 2 hours of match time.',
    stat: 'secondsPlayed',
    target: 7200,
    premiumOnly: true,
    reward: xp(800),
  },
  {
    id: 'wp-mvp-5',
    kind: 'weekly',
    difficulty: 'hard',
    name: 'Carry The Team',
    description: 'Earn 5 MVPs.',
    stat: 'mvps',
    target: 5,
    premiumOnly: true,
    reward: drop('import'),
  },
];

/** One season challenge, plus the stage it belongs to. */
export interface SeasonChallenge {
  readonly def: ChallengeDef;
  /** 1..SEASON_STAGES. */
  readonly stage: number;
}

const season = (
  stage: number,
  id: string,
  difficulty: ChallengeDef['difficulty'],
  name: string,
  description: string,
  stat: keyof MatchStats,
  target: number,
  reward: ChallengeReward
): SeasonChallenge => ({
  stage,
  def: { id, kind: 'season', difficulty, name, description, stat, target, reward },
});

/**
 * The season ladder: four stages of three, each stage a step up on the last.
 *
 * The targets climb rather than the stats changing, so a player can see where
 * the ladder is going before they are on it.
 */
export const SEASON_CHALLENGES: readonly SeasonChallenge[] = [
  season(1, 's1-matches-10', 'easy', 'Kick Off', 'Play 10 matches.', 'matches', 10, xp(500)),
  season(1, 's1-goals-15', 'easy', 'Opening Goals', 'Score 15 goals.', 'goals', 15, xp(500)),
  season(1, 's1-saves-10', 'easy', 'Hold The Line', 'Make 10 saves.', 'saves', 10, credits(30)),

  season(2, 's2-wins-15', 'medium', 'Winning Season', 'Win 15 matches.', 'wins', 15, credits(60)),
  season(2, 's2-assists-20', 'medium', 'Team Player', 'Get 20 assists.', 'assists', 20, xp(900)),
  season(
    2,
    's2-distance-1m',
    'medium',
    'Round The Clock',
    'Drive 1,000,000 uu.',
    'distance',
    1_000_000,
    xp(900)
  ),

  season(3, 's3-goals-75', 'medium', 'Prolific', 'Score 75 goals.', 'goals', 75, credits(80)),
  season(3, 's3-saves-50', 'hard', 'Keeper', 'Make 50 saves.', 'saves', 50, credits(80)),
  season(3, 's3-demos-50', 'hard', 'Menace', 'Demolish 50 opponents.', 'demolitions', 50, xp(1400)),

  season(4, 's4-mvp-20', 'hard', 'Most Valuable', 'Earn 20 MVPs.', 'mvps', 20, drop('exotic')),
  season(4, 's4-aerial-25', 'hard', 'Airborne', 'Score 25 aerial goals.', 'aerialGoals', 25, credits(120)),
  season(
    4,
    's4-backwards-10',
    'hard',
    'Facing The Wrong Way',
    'Score 10 goals driving backwards.',
    'backwardsGoals',
    10,
    credits(120)
  ),
];

/** One event challenge and the window, in days from the season start, it lives in. */
export interface EventChallenge {
  readonly def: ChallengeDef;
  /** Inclusive, days after the season start. */
  readonly startDay: number;
  /** Exclusive. */
  readonly endDay: number;
}

/**
 * Events, as offsets inside the season rather than absolute dates.
 *
 * An event pinned to a date in 2026 is an event that is over. Offsets mean the
 * same four windows come round in every season, which is both what the real
 * game does and the only version of this that is still alive next year.
 */
export const EVENT_CHALLENGES: readonly EventChallenge[] = [
  {
    startDay: 0,
    endDay: 14,
    def: {
      id: 'e-openers',
      kind: 'event',
      difficulty: 'medium',
      name: 'Season Openers',
      description: 'Win 6 matches in the first fortnight.',
      stat: 'wins',
      target: 6,
      reward: credits(50),
    },
  },
  {
    startDay: 21,
    endDay: 35,
    def: {
      id: 'e-demolition-days',
      kind: 'event',
      difficulty: 'medium',
      name: 'Demolition Days',
      description: 'Demolish 25 opponents while the event runs.',
      stat: 'demolitions',
      target: 25,
      reward: drop('rare'),
    },
  },
  {
    startDay: 42,
    endDay: 56,
    def: {
      id: 'e-aerial-week',
      kind: 'event',
      difficulty: 'hard',
      name: 'Air Show',
      description: 'Score 8 aerial goals while the event runs.',
      stat: 'aerialGoals',
      target: 8,
      reward: credits(90),
    },
  },
  {
    startDay: 70,
    endDay: 84,
    def: {
      id: 'e-final-push',
      kind: 'event',
      difficulty: 'hard',
      name: 'Final Push',
      description: 'Score 40 goals before the season ends.',
      stat: 'goals',
      target: 40,
      reward: drop('exotic'),
    },
  },
];

/** Every def this module knows about, for id lookup. */
export const ALL_CHALLENGE_DEFS: readonly ChallengeDef[] = [
  ...WEEKLY_FREE_POOL,
  ...WEEKLY_PREMIUM_POOL,
  ...SEASON_CHALLENGES.map(s => s.def),
  ...EVENT_CHALLENGES.map(e => e.def),
];

export const challengeById = (id: string): ChallengeDef | undefined =>
  ALL_CHALLENGE_DEFS.find(def => def.id === id);

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

/** Draw `count` distinct defs from a pool, seeded so a week is reproducible. */
const draw = (pool: readonly ChallengeDef[], count: number, rng: Rng): ChallengeDef[] => {
  const bag = [...pool];
  const out: ChallengeDef[] = [];
  const wanted = Math.min(count, bag.length);
  for (let i = 0; i < wanted; i++) {
    const pick = i + rng.nextInt(bag.length - i);
    const chosen = bag[pick];
    bag[pick] = bag[i];
    bag[i] = chosen;
    out.push(chosen);
  }
  return out;
};

/**
 * This week's three free challenges, and three more for premium holders.
 *
 * Deterministic in the week index alone, so every client shows the same week
 * and a reload cannot reroll a set the player did not like. The two rows draw
 * from separately salted generators, so unlocking premium mid-week does not
 * disturb the free three the player is already part-way through.
 */
export const weeklyChallenges = (week: number, premium: boolean): ChallengeDef[] => {
  const free = draw(
    WEEKLY_FREE_POOL,
    WEEKLY_FREE_COUNT,
    new Rng((Math.imul(week + 1, 0x9e37_79b1) ^ 0x1234_5678) >>> 0)
  );
  if (!premium) return free;
  const paid = draw(
    WEEKLY_PREMIUM_POOL,
    WEEKLY_PREMIUM_COUNT,
    new Rng((Math.imul(week + 1, 0x85eb_ca6b) ^ 0x0f1e_2d3c) >>> 0)
  );
  return [...free, ...paid];
};

/** True once every challenge in `stage` has reached its target. */
export const seasonStageComplete = (profile: PlayerProfile, stage: number): boolean => {
  const defs = SEASON_CHALLENGES.filter(s => s.stage === stage);
  if (defs.length === 0) return false;
  return defs.every(s => {
    const entry = profile.challenges.find(p => p.id === s.def.id);
    return entry !== undefined && entry.value >= s.def.target;
  });
};

/**
 * The highest season stage currently open, 1..SEASON_STAGES.
 *
 * Walks up from stage 1 and stops at the first incomplete one, so a stage
 * cannot be skipped by completing a later one out of order — which is possible,
 * because a match's stats feed every open challenge at once.
 */
export const unlockedSeasonStage = (profile: PlayerProfile): number => {
  let stage = 1;
  while (stage < SEASON_STAGES && seasonStageComplete(profile, stage)) stage++;
  return stage;
};

/** The event challenges live at `nowMs`, with the window they close in. */
export const activeEvents = (nowMs: number): { def: ChallengeDef; endsAt: number }[] => {
  const { startsAt } = seasonWindow(nowMs);
  const out: { def: ChallengeDef; endsAt: number }[] = [];
  for (const event of EVENT_CHALLENGES) {
    const from = startsAt + event.startDay * DAY_MS;
    const to = startsAt + event.endDay * DAY_MS;
    if (nowMs >= from && nowMs < to) out.push({ def: event.def, endsAt: to });
  }
  return out;
};

// ---------------------------------------------------------------------------
// Refresh
// ---------------------------------------------------------------------------

/** A def together with the moment its progress entry stops counting. */
export interface ActiveChallenge {
  def: ChallengeDef;
  expiresAt: number;
}

/**
 * Everything the player should have a live progress entry for, right now.
 *
 * Season stages beyond the unlocked one are absent rather than locked-and-
 * visible: their progress must not accrue before they open, or a player would
 * unlock stage 3 already finished.
 */
export const activeChallenges = (profile: PlayerProfile, nowMs: number): ActiveChallenge[] => {
  const out: ActiveChallenge[] = [];
  const expiry = weekEnd(nowMs);

  for (const def of weeklyChallenges(weekIndex(nowMs), profile.progress.passPremium)) {
    out.push({ def, expiresAt: expiry });
  }

  const seasonEnd = seasonWindow(nowMs).endsAt;
  const stage = unlockedSeasonStage(profile);
  for (const entry of SEASON_CHALLENGES) {
    if (entry.stage <= stage) out.push({ def: entry.def, expiresAt: seasonEnd });
  }

  for (const event of activeEvents(nowMs)) {
    out.push({ def: event.def, expiresAt: event.endsAt });
  }

  return out;
};

/**
 * Bring `profile.challenges` into line with the calendar.
 *
 * Expired entries go, whether or not they were claimed — an unclaimed weekly
 * that has run out is gone in the real game too, and keeping it would quietly
 * turn the weekly into a permanent one. New entries arrive at zero.
 *
 * Idempotent: calling it twice with the same `nowMs` changes nothing the second
 * time, which matters because it runs on every visit to the challenge screen
 * as well as after every match.
 */
export const refreshChallenges = (profile: PlayerProfile, nowMs: number): PlayerProfile => {
  const active = activeChallenges(profile, nowMs);
  const wanted = new Set(active.map(a => a.def.id));

  const kept = profile.challenges.filter(
    entry => wanted.has(entry.id) && entry.expiresAt > nowMs
  );
  const held = new Set(kept.map(entry => entry.id));
  const added = active
    .filter(a => !held.has(a.def.id))
    .map(a => ({ id: a.def.id, value: 0, claimed: false, expiresAt: a.expiresAt }));

  if (added.length === 0 && kept.length === profile.challenges.length) return profile;
  return { ...profile, challenges: [...kept, ...added] };
};

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export const isComplete = (entry: ChallengeProgress, def: ChallengeDef): boolean =>
  entry.value >= def.target;

/**
 * Fold one match's statistics into every live challenge.
 *
 * `mode` is checked against `def.modes`, which is how a playlist-specific
 * challenge is expressed; a def without `modes` counts everywhere. Claimed
 * entries and complete ones stop accruing, so a finished counter reads
 * "10 / 10" rather than "37 / 10".
 *
 * Returns the entries that became complete on THIS call, which is what the
 * post-match summary shows — "you finished two challenges" is a fact about the
 * match, not about the profile.
 */
export interface StatsApplied {
  profile: PlayerProfile;
  /** The entries that became complete on THIS call. */
  completed: ChallengeProgress[];
}

export const applyMatchStats = (
  profile: PlayerProfile,
  stats: MatchStats,
  nowMs: number,
  mode?: string
): StatsApplied => {
  const completed: ChallengeProgress[] = [];

  const challenges = profile.challenges.map(entry => {
    if (entry.claimed || entry.expiresAt <= nowMs) return entry;
    const def = challengeById(entry.id);
    if (def === undefined) return entry;
    if (def.modes !== undefined && mode !== undefined && !def.modes.includes(mode)) return entry;

    const delta = stats[def.stat];
    if (!Number.isFinite(delta) || delta <= 0) return entry;

    const next = { ...entry, value: Math.min(def.target, entry.value + delta) };
    if (entry.value < def.target && next.value >= def.target) completed.push(next);
    return next;
  });

  return { profile: { ...profile, challenges }, completed };
};

// ---------------------------------------------------------------------------
// Claiming
// ---------------------------------------------------------------------------

export type ChallengeClaimFailure = 'unknown' | 'expired' | 'incomplete' | 'already-claimed';

export interface ChallengeClaimResult {
  ok: boolean;
  reason?: ChallengeClaimFailure;
  /** Tier Points awarded. `CHALLENGE_TIER_POINTS` for the difficulty. */
  tierPoints: number;
  /** Tiers crossed by those points. */
  tiers: number;
  profile: PlayerProfile;
}

/** Why an entry cannot be claimed right now, or null. Drives the row's state. */
export const claimProblem = (
  profile: PlayerProfile,
  id: string,
  nowMs: number
): ChallengeClaimFailure | null => {
  const entry = profile.challenges.find(p => p.id === id);
  const def = challengeById(id);
  if (entry === undefined || def === undefined) return 'unknown';
  if (entry.claimed) return 'already-claimed';
  if (entry.expiresAt <= nowMs) return 'expired';
  if (!isComplete(entry, def)) return 'incomplete';
  return null;
};

/**
 * Claim a completed challenge: the reward, then the Tier Points.
 *
 * The Tier Points are the point of the exercise — `CHALLENGE_TIER_POINTS` is
 * 3/5/8 by difficulty in the contract, and this is the only place challenges
 * feed the pass.
 *
 * `rng` is only touched by a `drop` reward, and it is the caller's so a claim
 * is reproducible in a test and a reload cannot re-roll one that has happened.
 */
export const claimChallenge = (
  profile: PlayerProfile,
  id: string,
  nowMs: number,
  rng: Rng
): ChallengeClaimResult => {
  const problem = claimProblem(profile, id, nowMs);
  if (problem !== null) return { ok: false, reason: problem, tierPoints: 0, tiers: 0, profile };

  const def = challengeById(id);
  if (def === undefined) return { ok: false, reason: 'unknown', tierPoints: 0, tiers: 0, profile };

  let next = applyChallengeReward(profile, def.reward, rng);
  next = {
    ...next,
    challenges: next.challenges.map(entry =>
      entry.id === id ? { ...entry, claimed: true } : entry
    ),
    progress: { ...next.progress },
  };

  const tierPoints = CHALLENGE_TIER_POINTS[def.difficulty];
  const gain = addTierPoints(next.progress, tierPoints);
  return { ok: true, tierPoints, tiers: gain.tiers, profile: next };
};

/**
 * Hand a challenge reward over.
 *
 * A `drop` reward that the catalogue cannot satisfy rolls null and grants
 * nothing — ./drops is explicit that null means "no drop" and never an error,
 * because a reward that cannot be granted must not take a match summary down.
 */
const applyChallengeReward = (
  profile: PlayerProfile,
  reward: ChallengeReward,
  rng: Rng
): PlayerProfile => {
  switch (reward.kind) {
    case 'xp': {
      const next: PlayerProfile = { ...profile, progress: { ...profile.progress } };
      grantXp(next.progress, reward.amount);
      return next;
    }
    case 'credits':
      return {
        ...profile,
        wallet: { ...profile.wallet, credits: profile.wallet.credits + reward.amount },
      };
    case 'item':
      return grantItem(profile, reward.itemId);
    case 'drop': {
      const roll = rollDrop(rng, { rarity: reward.rarity });
      return roll === null ? profile : grantDrops(profile, [roll]).profile;
    }
  }
};

/** Every live entry that is finished and not yet taken. */
export const claimableChallenges = (
  profile: PlayerProfile,
  nowMs: number
): ChallengeProgress[] =>
  profile.challenges.filter(entry => {
    if (entry.claimed || entry.expiresAt <= nowMs) return false;
    const def = challengeById(entry.id);
    return def !== undefined && isComplete(entry, def);
  });

/**
 * Tier Points still on the table this week, for the pass screen's forecast.
 *
 * Counts only what is live: an expired weekly is not "available", and a season
 * stage that has not opened yet is not either.
 */
export const availableTierPoints = (profile: PlayerProfile, nowMs: number): number => {
  let total = 0;
  for (const entry of profile.challenges) {
    if (entry.claimed || entry.expiresAt <= nowMs) continue;
    const def = challengeById(entry.id);
    if (def !== undefined) total += CHALLENGE_TIER_POINTS[def.difficulty];
  }
  return total;
};
